import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HRSystemConfig } from '@/lib/hr-integrations/types';
import { AtlassianJobBoard } from '@/components/jobs/AtlassianJobBoard';

interface ImportJobsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

export function ImportJobsDialog({ 
  open, 
  onOpenChange,
  onImportComplete 
}: ImportJobsDialogProps) {
  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [config, setConfig] = useState<HRSystemConfig>({
    type: 'workday',
    apiKey: '',
    apiEndpoint: '',
  });

  const handleSystemChange = (value: string) => {
    setSelectedSystem(value);
    setConfig({ ...config, type: value as HRSystemConfig['type'] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>Import Jobs from HR System</DialogTitle>
          <DialogDescription>
            Connect to your HR system to import job listings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">HR System</label>
            <Select
              value={selectedSystem}
              onValueChange={handleSystemChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select HR system" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="workday">Workday</SelectItem>
                <SelectItem value="atlassian">Atlassian</SelectItem>
                <SelectItem value="successfactors">SAP SuccessFactors</SelectItem>
                <SelectItem value="adp">ADP</SelectItem>
                <SelectItem value="greenhouse">Greenhouse</SelectItem>
                <SelectItem value="lever">Lever</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedSystem && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {selectedSystem === 'atlassian' ? 'API Token' : 'API Key'}
                </label>
                <Input
                  type="password"
                  value={config.apiKey}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                  placeholder={selectedSystem === 'atlassian' ? 'Enter API token' : 'Enter API key'}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {selectedSystem === 'atlassian' ? 'Jira Cloud URL' : 'API Endpoint'}
                </label>
                <Input
                  value={config.apiEndpoint}
                  onChange={(e) => setConfig({ ...config, apiEndpoint: e.target.value })}
                  placeholder={selectedSystem === 'atlassian' 
                    ? 'https://your-domain.atlassian.net'
                    : 'https://api.example.com'
                  }
                />
              </div>
              {selectedSystem === 'atlassian' && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Make sure you have a "Recruitment" project set up in Jira with appropriate custom fields for job details.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {selectedSystem === 'atlassian' && config.apiKey && config.apiEndpoint && (
            <AtlassianJobBoard
              apiKey={config.apiKey}
              endpoint={config.apiEndpoint}
              onImportComplete={onImportComplete}
            />
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}